import { useEffect, useState, SyntheticEvent } from 'react';

import { Modal, Box } from '@material-ui/core';

import { getAcronyms, updateAcronym } from '../apis/acronym';
import AcronymFilters from '../components/Filters/Filters';
import AcronymTable from '../components/Acronym/AcronymTable';
import { AcronymNameType, AcronymType } from '../types/AcronymTypes';
import { AcronymTypeObject } from './../types/AcronymTypes';
import ModalHeader from './../components/Modal/ModalHeader';
import { ModalBody } from 'react-bootstrap';
import AcronymForm from '../components/Acronym/AcronymForm';
import { acronymPeriodInDays } from './../config/index';
import { Alert } from '@material-ui/lab';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setAcronymsFilter, setFilteredAcronyms } from '../redux/actions';

const currentTime = new Date().getTime();
const oneDay = 1000 * 60;

const getAcronymsDays = (acronyms: AcronymType[]): number[] => {
  return acronyms.map((acronym) => {
    const created = acronym.created || '';
    const diffInTime = currentTime - new Date(created).getTime();
    const diffInDays = Math.round(diffInTime / oneDay);
    return diffInDays;
  });
};

const getAcronymStatistics = (acronyms: AcronymType[]) => {
  const num = acronyms.length;
  const days = getAcronymsDays(acronyms);

  const oldest = Math.max.apply(null, days);
  const average = Math.round(days.reduce((a, b) => a + b, 0) / num);

  return { num, oldest, average };
};

const Acronym = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(new Date().setDate(new Date().getDate() - acronymPeriodInDays)),
  );
  const [response, setResponse] = useState<any>({
    errors: null,
    data: [],
  });
  const [acronym, setAcronym] = useState<AcronymType>(new AcronymTypeObject());
  const [acronyms, setAcronyms] = useState(response.data.filter((item) => item.type !== AcronymNameType.Number));
  const acronymFilter = useAppSelector((state) => state.acronyms.filter);
  const globalAcronymData = useAppSelector((state) => state.acronyms.acronyms);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const res = await getAcronyms(startDate, endDate);

      if (res?.data) {
        setResponse(res);
      }

      if (res?.errors) {
        alert('Something went wrong');
      }

      setLoading(false);
    };

    loadData();
  }, [startDate, endDate]);

  useEffect(() => {
    if (localStorage.getItem('acronym-filter') !== null && localStorage.getItem('acronym-filter')) {
      dispatch(setAcronymsFilter(localStorage.getItem('acronym-filter')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (acronymFilter !== null) {
      dispatch(setFilteredAcronyms(acronyms.filter((item) => item.type.toLowerCase() === acronymFilter)));
    } else {
      setAcronyms(response.data.filter((item) => item.type !== AcronymNameType.Number));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acronymFilter]);

  useEffect(() => {
    setAcronyms(response.data.filter((item) => item.type !== AcronymNameType.Number));
    dispatch(
      setFilteredAcronyms(
        response.data
          .filter((item) => item.type !== AcronymNameType.Number)
          .filter((item) => item.type.toLowerCase() === acronymFilter),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data]);

  const { num, oldest, average } = getAcronymStatistics(acronyms);

  const dispatch = useAppDispatch();

  const updateAcronymRow = async (acr: AcronymType) => {
    setResponse({ ...response, errors: null });
    setLoading(true);
    setOpen(false);

    const { data, errors } = await updateAcronym(acronym.name, {
      ...acronym,
      ...acr,
    });
    const acronymData = acronymFilter !== null ? globalAcronymData : acronyms;
    const acronymsUpdate = [...acronyms];

    if (data) {
      const index = acronymsUpdate.findIndex((item) => item.name === data.name && item.documentId === data.documentId);
      const filteredIndex = acronymData.findIndex((item) => item.name === data.name && item.documentId === data.documentId);
      //console.log('index', index);
      acronymsUpdate.splice(index, 1);

      setResponse({
        errors: null,
        data: [...acronymsUpdate],
        // data: [...acronymsUpdate.filter(
        //   (item) => !(item.addToDict || item.skip || item.followUp)
        // )]
      });
      const newAcronyms = acronymsUpdate.filter((item) => item.type !== AcronymNameType.Number);
      setAcronyms(newAcronyms);

      let nextIndex;
      if (acronymFilter !== null) {
        const filteredAcronymsUpdate = acronymsUpdate.filter((item) => item.type.toLowerCase() === acronymFilter);
        nextIndex =
          filteredIndex + 1 < filteredAcronymsUpdate.length
            ? filteredIndex + 1
            : filteredAcronymsUpdate.length - 1 > 0
            ? 0
            : undefined;
      } else {
        nextIndex = index + 1 < acronymsUpdate.length ? index + 1 : acronymsUpdate.length - 1 > 0 ? 0 : undefined;
      }

      // nextIndex = index + 1 < acronymData.length ? index + 1 : acronymData.length - 1 > 0 ? 0 : undefined;
      //console.log('nextIndex', nextIndex);

      if (nextIndex !== undefined) {
        onRowClick(acronymData[nextIndex]);
        //console.log('nextData', acronymData[nextIndex]);
        setOpen(true);
      } else {
        setOpen(false);
      }
    }

    if (errors) {
      setResponse({ errors: 'Something went wrong', data: acronyms });
      setOpen(true);
    }

    setLoading(false);
  };

  const onChangeEndDate = async (date: Date | null) => {
    setEndDate(date);
  };

  const onChangeStartDate = async (date: Date | null) => {
    setStartDate(date);
  };

  const onRowClick = (data: AcronymType) => {
    let pronounce = data.pronounce;

    if (data.type === AcronymNameType.Jargon) {
      pronounce = data?.name
        ?.split('.')
        .join('')
        .replace(/(.{1})/g, '$1.');
    }

    setOpen(true);
    setAcronym({
      ...data,
      pronounce: pronounce || '',
    });
  };

  const updateName = (e: SyntheticEvent) => {
    const name = (e.target as HTMLInputElement).value;
    setAcronym({
      ...acronym,
      name,
    });
  };

  const updatePronounce = (e: SyntheticEvent) => {
    const pronounce = (e.target as HTMLInputElement).value;
    setAcronym({
      ...acronym,
      pronounce,
    });
  };

  const updateFollowUp = (e: SyntheticEvent) => {
    const followUp = (e.target as HTMLInputElement).checked;
    setAcronym({
      ...acronym,
      followUp,
    });
  };

  return (
    <div className="Acronym">
      <AcronymFilters
        className="Acronym__Filters"
        startDate={startDate}
        onChangeStartDate={onChangeStartDate}
        endDate={endDate}
        onChangeEndDate={onChangeEndDate}
      />
      {acronyms && (
        <AcronymTable
          title={
            <>
              <div className="table-header">Acronyms</div>
              {acronyms.length > 0 && (
                <>
                  <div className="table-sub-header">
                    <strong>{num}</strong> items pending for review • Average time in queue <strong>{average}</strong>{' '}
                    minutes • Oldest item <strong>{oldest}</strong> minutes ago
                  </div>
                </>
              )}
            </>
          }
          data={acronyms}
          onRowClick={onRowClick}
          loading={loading}
        />
      )}
      {response?.errors && (
        <Alert variant="filled" severity="error">
          {response?.errors}
        </Alert>
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal-wrapper">
          {open && (
            <>
              <ModalHeader title={acronym?.name} onClose={() => setOpen(false)} />
              <ModalBody>
                <AcronymForm
                  acronym={acronym}
                  updateAcronymRow={updateAcronymRow}
                  updateName={updateName}
                  updatePronounce={updatePronounce}
                  updateFollowUp={updateFollowUp}
                  allowAddToDict={true}
                  allowSkip={true}
                  canEditField={true}
                />
              </ModalBody>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default Acronym;
